import PropTypes from 'prop-types';
// BASE ------------------------------------------------------------------------
import React, { useRef, useEffect, useCallback } from 'react';

import Dump from 'components/Dump';
// SELECTORS, ACTIONS, UTILS ------------------------------------------------------------------------
import useSpeechRecognition from 'utils/speech/speechRecognition';
import useSpeechSynthesis from 'utils/speech/useSpeechSynthesis';
import useVoiceState from 'utils/speech/useVoiceState';
import { useChatPromptField } from './hooks/useChatPromptField';
import { useExpandableTextField } from './hooks/useExpandableTextField';

// CUSTOM COMPONENTS ------------------------------------------------------------------------

import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
// MUI COMPONENTS ------------------------------------------------------------------------
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';

const MAX_ROWS = 12;

export const ChatPromptField = ({
  initialValue,
  onSubmit: onSubmitProp,
  errorText,
  label,
  disabled,
  placeholder,
  autoConversation,
  setAutoConversation,
}) => {
  const { fieldValue, handleChange, setFieldValue } = useChatPromptField(initialValue);
  const { rows, setRows, handleRowsChange } = useExpandableTextField(MAX_ROWS);
  const onSubmit = useCallback(
    (value) => {
      onSubmitProp?.(value);
      setRows(1);
    },
    [onSubmitProp, setRows],
  );

  const [voiceState] = useVoiceState();
  const speak = useSpeechSynthesis();
  const onEnd = useCallback(() => {
    setAutoConversation(true);
    if (fieldValue) {
      onSubmit?.(fieldValue);
    } else {
      setAutoConversation(false);
    }
  }, [onSubmit, fieldValue, setAutoConversation]);
  const { startRecognition, stopRecognition } = useSpeechRecognition(setFieldValue, onEnd);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!disabled) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (onSubmit) onSubmit(fieldValue);
    }
  };

  const handleClick = () => {
    if (onSubmit) onSubmit(fieldValue);
  };

  return (
    <Stack gap={1}>
      <Dump value={{ voiceState, autoConversation }} id='voice-state' />
      <Stack direction='row' gap={2} alignItems={'center'}>
        <TextField
          size='small'
          hiddenLabel
          fullWidth
          multiline
          minRows={rows}
          maxRows={MAX_ROWS}
          placeholder={placeholder}
          disabled={disabled}
          inputRef={inputRef}
          onKeyDown={handleKeyPress}
          value={fieldValue}
          error={Boolean(errorText)}
          onChange={(event) => (handleChange(event), handleRowsChange(event))}
          variant='filled'
          helperText={errorText}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end' sx={{ display: 'inline', alignItems: 'center' }}>
                <IconButton
                  size='small'
                  disabled={!fieldValue}
                  variant='contained'
                  onClick={handleClick}
                  sx={{ width: '24px', height: '24px', transform: 'translateY(-50%)' }}
                >
                  <ArrowUpwardRounded />
                </IconButton>
              </InputAdornment>
            ),
            sx: (theme) => ({
              '& .MuiInputBase-input': {
                ...theme.typography.paragraphRegular,
                padding: theme.spacing(1.25),
              },
            }),
          }}
        />
        {setAutoConversation && (
          <IconButton
            size='small'
            sx={{ width: '24px', height: '24px' }}
            variant='tonal'
            aria-label='speech-recognition'
            edge='end'
            onClick={() => {
              switch (voiceState) {
                case 'IDLE':
                  if (startRecognition) {
                    speak(null);
                    const sound = new Audio(
                      'data:audio/mpeg;base64,//tQwAAAAAABLBQAAAFoJmO7KUAAAIAAAAADADABBBCAAA8AoIAUI8i4HuSa3A2O8Dzpd2Al4A1538DBBiKEx/mg7xHAnD/wvYKDSN0v/0DQhg4BURcf/+MwggyJwnP//yqViCEQXqb///zzmhUTZaJDAAAgEARAk9vt9ocAANarNa5adK6005YWKshNGW+4ZGGdE2ZJ8NLpJgC4fHIL+yF/koSYmQopoMh4K6OI0N05qmrXdY9yeO4YUAqg6kzdBy+lUtVx2jITY3Lg70DyaP/7UsAwgBANA6H5pthZTJSt96RgAxoybVeFaFELEcKRoozJdNBjRN3MG/6aBmS/1mU4ARTyQAAACW67YBBJqP6iHBgYDXw9Udq9bd////8a9S5lNV5pbxU15ovSJlonWMJFLRO2a8zGvlPFHBoWSSigtEGyFUUWUqiiyg5JLCFAhVEGjCpJKiD6SAAACW7f8CpOeVhYyOwXpI2ORRXQ2V3////mnqPTdnHPd9dvexKxbWnbxm8ZNn+v5Wl6qW9GZ6jD5mjwXNMfJ3Kql+mh2cAA//tSwCSACJgjaaeLItkPI6488AnfAAXa6Who03rER9WkF7/igB647+U1KyqyURjuUimdyPkq/302JYiPdWsV3oXo+yU+lnedlWwZHRys5eKM9ExfMP/qAAA5tdbQDwyQMkDFBFJLUZB8Rs3l9/kR2aWx1GNTRVaV7H1Ie9HqRDyhefMRwPSy4D7/lL+OL3Rb7pzxa7fy5qU47l2oAAAu11tAV2nuYlfiACJf+WWs/dZkaRjyzy1/JNCLWGTNZa6+hQiJtmMTwith0tyIjizNgz7/+1LAPgAImLdvqABOuQ+hrXSADd92nhUJESioZVUfvKXQu2wAAAu1ttAAUfKqijF/xCD9L6pmeexmbGolD2novPn2vL7rrPO5xUZj/IjYkYVtTgE1NWqtTcoxW7cKQzVRLnwMe9Gw1VgKUAAU3JbQADLnkSLaDGjUoFLnJjgorPofj8gnqVGZmUowEKGxgo6KCjYoKKxBXcCjeBQVkFDcQUbFBTegpvAoFyCgr8jvBQoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/7UsBXgAjZF2WkAG75IQhl9GwNUQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAg//tSwG4AAAABLAAAAAAAACWAAAAAACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACD/+1LAzAAAAAEsAAAAAAAAJYAAAAAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAIAAgACAAAA==',
                    );
                    sound.play();
                    startRecognition();
                    setAutoConversation(true);
                  }
                  break;
                case 'LISTENING':
                  stopRecognition?.();
                  setAutoConversation(false);
                  break;
                case 'SPEAKING':
                  speak(null);
                  setAutoConversation(false);
                  break;
                default:
                  break;
              }
            }}
          >
            {voiceState === 'IDLE' && <MicNoneOutlinedIcon />}
            {voiceState === 'LISTENING' && <RecordVoiceOverOutlinedIcon />}
            {voiceState === 'SPEAKING' && <VolumeOffOutlinedIcon />}
          </IconButton>
        )}
      </Stack>
      <Typography variant='captionRegular' textAlign='center' color='text.secondary'>
        {label}
      </Typography>
    </Stack>
  );
};

ChatPromptField.propTypes = {
  initialValue: PropTypes.string,
  // classes: PropTypes.object,
  onSubmit: PropTypes.func,
  errorText: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  autoConversation: PropTypes.object,
  setAutoConversation: PropTypes.func,
};
export default ChatPromptField;
